import React from 'react';
import { cn } from '@/utils/cn';

export type AdType = 'RESPONSIVE_SEARCH_AD';

interface AdCreationStepProps {
  headlines: string[];
  descriptions: string[];
  finalUrl: string;
  path1: string;
  path2: string;
  onChangeHeadlines: (headlines: string[]) => void;
  onChangeDescriptions: (descriptions: string[]) => void;
  onChangeFinalUrl: (url: string) => void;
  onChangePath1: (path: string) => void;
  onChangePath2: (path: string) => void;
  errors?: {
    headlines?: string;
    descriptions?: string;
    finalUrl?: string;
  };
}

export const AdCreationStep: React.FC<AdCreationStepProps> = ({
  headlines,
  descriptions,
  finalUrl,
  path1,
  path2,
  onChangeHeadlines,
  onChangeDescriptions,
  onChangeFinalUrl,
  onChangePath1,
  onChangePath2,
  errors,
}) => {
  const handleHeadlineChange = (index: number, value: string) => {
    const updated = [...headlines];
    updated[index] = value;
    onChangeHeadlines(updated);
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updated = [...descriptions];
    updated[index] = value;
    onChangeDescriptions(updated);
  };

  const addHeadline = () => {
    if (headlines.length < 15) {
      onChangeHeadlines([...headlines, '']);
    }
  };

  const removeHeadline = (index: number) => {
    if (headlines.length > 3) {
      onChangeHeadlines(headlines.filter((_, i) => i !== index));
    }
  };

  const addDescription = () => {
    if (descriptions.length < 4) {
      onChangeDescriptions([...descriptions, '']);
    }
  };

  const removeDescription = (index: number) => {
    if (descriptions.length > 2) {
      onChangeDescriptions(descriptions.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Your First Ad</h3>
        <p className="text-sm text-gray-600">
          Ad groups require at least one ad. Create a Responsive Search Ad below.
        </p>
      </div>

      {/* Ad Type Info */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center">
          <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-900">Responsive Search Ad</span>
            <p className="text-xs text-gray-500">Google automatically optimizes headline and description combinations for best performance</p>
          </div>
        </div>
      </div>

      {/* RSA Form */}
      <div className="space-y-6 pt-4 border-t border-gray-200">
          {/* Final URL */}
          <div>
            <label htmlFor="final-url" className="block text-sm font-medium text-gray-700 mb-2">
              Final URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              id="final-url"
              value={finalUrl}
              onChange={(e) => onChangeFinalUrl(e.target.value)}
              placeholder="https://www.example.com/landing-page"
              className={cn(
                'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                { 'border-red-500': errors?.finalUrl }
              )}
            />
            {errors?.finalUrl && <p className="mt-1 text-sm text-red-600">{errors.finalUrl}</p>}
            <p className="mt-1 text-xs text-gray-500">The landing page URL where users will go</p>
          </div>

          {/* Path Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="path1" className="block text-sm font-medium text-gray-700 mb-2">
                Path 1 (optional)
              </label>
              <input
                type="text"
                id="path1"
                value={path1}
                onChange={(e) => onChangePath1(e.target.value)}
                placeholder="deals"
                maxLength={15}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">Display path (15 chars max)</p>
            </div>
            <div>
              <label htmlFor="path2" className="block text-sm font-medium text-gray-700 mb-2">
                Path 2 (optional)
              </label>
              <input
                type="text"
                id="path2"
                value={path2}
                onChange={(e) => onChangePath2(e.target.value)}
                placeholder="shoes"
                maxLength={15}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">Display path (15 chars max)</p>
            </div>
          </div>

          {/* Headlines */}
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Headlines <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-gray-500">
                {headlines.length}/15 (min 3 required)
              </span>
            </div>
            <div className="space-y-2">
              {headlines.map((headline, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={headline}
                    onChange={(e) => handleHeadlineChange(index, e.target.value)}
                    placeholder={`Headline ${index + 1}`}
                    maxLength={30}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-500 self-center min-w-[40px]">
                    {headline.length}/30
                  </span>
                  {headlines.length > 3 && (
                    <button
                      type="button"
                      onClick={() => removeHeadline(index)}
                      className="text-red-600 hover:text-red-700 px-2"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors?.headlines && <p className="mt-1 text-sm text-red-600">{errors.headlines}</p>}
            {headlines.length < 15 && (
              <button
                type="button"
                onClick={addHeadline}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                + Add headline
              </button>
            )}
          </div>

          {/* Descriptions */}
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Descriptions <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-gray-500">
                {descriptions.length}/4 (min 2 required)
              </span>
            </div>
            <div className="space-y-2">
              {descriptions.map((description, index) => (
                <div key={index} className="flex gap-2">
                  <textarea
                    value={description}
                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                    placeholder={`Description ${index + 1}`}
                    maxLength={90}
                    rows={2}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-500 self-start mt-2 min-w-[40px]">
                    {description.length}/90
                  </span>
                  {descriptions.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeDescription(index)}
                      className="text-red-600 hover:text-red-700 px-2 self-start mt-2"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors?.descriptions && (
              <p className="mt-1 text-sm text-red-600">{errors.descriptions}</p>
            )}
            {descriptions.length < 4 && (
              <button
                type="button"
                onClick={addDescription}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                + Add description
              </button>
            )}
          </div>

          {/* Image assets info message */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <svg
                className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Want to add images?</span>{' '}
                  You can add image assets after creating the ad group through the{' '}
                  <span className="font-medium">Assets</span> tab in the campaign management view.
                </p>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};
